import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import socketService from '../services/socketService';
// Disable all toast notifications by replacing with no-op functions
const toast = {
  success: () => {},
  error: () => {},
  loading: () => {},
  promise: () => {},
  dismiss: () => {},
  remove: () => {}
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is logged in on app start
  useEffect(() => {
    const initializeAuth = () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      
      // console.log('AuthContext init - token:', token ? 'exists' : 'none', 'savedUser:', savedUser ? 'exists' : 'none'); // Debug log
      
      if (token && savedUser) {
        try {
          const userData = JSON.parse(savedUser);
          // console.log('AuthContext init - parsed user:', userData); // Debug log
          setUser(userData);
          setIsAuthenticated(true);
          
          // WebSocket connections are disabled - do nothing
          // socketService.connect(token, userData.id, userData.role);
          
          // console.log('AuthContext init - set authenticated to true'); // Debug log
        } catch (error) {
          console.error('Error parsing saved user:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };

    // Add a small delay to ensure localStorage is available
    setTimeout(initializeAuth, 100);
  }, []);

  // Login function
  const login = async (credentials) => {
    try {
      setLoading(true);
      const response = await authAPI.login(credentials);
      
      // console.log('Login response:', response.data); // Debug log
      
      if (response.data.success) {
        const { user: userData, accessToken } = response.data.data;
        
        // console.log('User data:', userData); // Debug log
        // console.log('Access token:', accessToken); // Debug log
        
        // Save to localStorage
        localStorage.setItem('token', accessToken);
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Update state
        setUser(userData);
        setIsAuthenticated(true);
        
        // WebSocket connections are disabled - do nothing
        // socketService.connect(accessToken, userData.id, userData.role);
        
        // WebSocket communications are disabled - do nothing
        // Emit user login event
        // socketService.emitUserLogin({
        //   userId: userData.id,
        //   userName: `${userData.firstName} ${userData.lastName}`,
        //   userRole: userData.role,
        //   timestamp: new Date().toISOString()
        // });
        
        // console.log('Auth state updated - isAuthenticated:', true); // Debug log
        
        return { success: true, user: userData };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      const message = error.response?.data?.message || 'Login failed';
      
      // Don't show toast for any login errors as Login component will handle them
      
      // Re-throw the error so Login component can handle specific status codes
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    // WebSocket communications are disabled - do nothing
    // Emit user logout event before disconnecting
    // if (user) {
    //   socketService.emitUserLogout({
    //     userId: user.id,
    //     userName: `${user.firstName} ${user.lastName}`,
    //     userRole: user.role,
    //     timestamp: new Date().toISOString()
    //   });
    // }
    
    // WebSocket connections are disabled - do nothing
    // Disconnect WebSocket
    // socketService.disconnect();
    
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
  };

  // Register function (admin only)
  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await authAPI.register(userData);
      
      if (response.data.success) {
        // Toast notifications are disabled
        // toast.success('User registered successfully!');
        return { success: true, user: response.data.user };
      } else {
        // Toast notifications are disabled
        // toast.error(response.data.message || 'Registration failed');
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Registration error:', error);
      const message = error.response?.data?.message || 'Registration failed';
      // Toast notifications are disabled
      // toast.error(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  // Get user profile
  const getProfile = async () => {
    try {
      const response = await authAPI.getProfile();
      if (response.data.success) {
        const userData = response.data.user;
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        return userData;
      }
    } catch (error) {
      console.error('Get profile error:', error);
      if (error.response?.status === 401) {
        logout();
      }
    }
  };

  // Check if user has specific role
  const hasRole = (roles) => {
    if (!user) return false;
    if (Array.isArray(roles)) {
      return roles.includes(user.role);
    }
    return user.role === roles;
  };

  // Check if user is admin
  const isAdmin = () => {
    return hasRole(['SUPER_ADMIN', 'ADMIN']);
  };

  // Check if user is super admin
  const isSuperAdmin = () => {
    return hasRole('SUPER_ADMIN');
  };

  // Check if user is student
  const isStudent = () => {
    return hasRole('STUDENT');
  };

  // Check if user is moderator
  const isModerator = () => {
    return hasRole('MODERATOR');
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    register,
    getProfile,
    hasRole,
    isAdmin,
    isSuperAdmin,
    isStudent,
    isModerator
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};